import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Robot, ArrowRight, ArrowLeft, ArrowClockwise, Printer, Copy, FloppyDisk, DownloadSimple, Sparkle, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const LANGUAGES = ["English", "Marathi", "Hindi", "Marathi + English", "Hindi + English"];

export default function InterviewMode() {
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [language, setLanguage] = useState("English");
  const [answers, setAnswers] = useState({}); // { key: value }
  const [history, setHistory] = useState([]); // [{key,label,value,input_type,options}] for back/skip
  const [current, setCurrent] = useState(null); // {key,label,input_type,options,hint}
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [inputValue, setInputValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftId, setDraftId] = useState("");
  const [phase, setPhase] = useState("setup"); // setup | asking | done

  useEffect(() => {
    api.get("/ai/templates").then((r) => setGroups(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  const selectedTemplate = useMemo(() => {
    for (const g of groups) {
      const t = g.items.find((x) => x.id === templateId);
      if (t) return t;
    }
    return null;
  }, [templateId, groups]);

  const startInterview = async () => {
    if (!templateId) { toast.error("Choose a template first"); return; }
    setAnswers({}); setHistory([]); setDraft(""); setDraftId(""); setInputValue("");
    setProgress({ current: 0, total: 0 });
    setPhase("asking");
    await askNext({});
  };

  const askNext = async (answersToSend, forceFinalize = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/interview/next", {
        template_id: templateId,
        customer_id: customerId && customerId !== "_none" ? customerId : "",
        language,
        answers: answersToSend,
        force_finalize: forceFinalize,
      });
      setProgress((prev) => {
        const p = data.progress || { current: 0, total: 0 };
        // Clamp monotonically — never let the % go backwards on a single turn.
        return {
          current: Math.max(prev.current || 0, p.current || 0),
          total: Math.max(prev.total || 0, p.total || 0, (p.current || 0) + 1),
        };
      });
      if (data.done) {
        setDraft(data.draft || "");
        setDraftId(data.draft_id || "");
        setCurrent(null);
        setPhase("done");
        toast.success("Draft ready — please review before printing");
      } else {
        setCurrent(data.question);
        setInputValue("");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI is unreachable — please retry.");
    } finally {
      setBusy(false);
    }
  };

  const submitAnswer = async () => {
    if (!current) return;
    const key = current.key;
    const val = String(inputValue ?? "").trim();
    if (!val) { toast.error("Please provide an answer or click Skip"); return; }
    const next = { ...answers, [key]: val };
    setAnswers(next);
    setHistory((h) => [...h, { ...current, value: val }]);
    await askNext(next);
  };

  const skipQuestion = async () => {
    if (!current) return;
    setHistory((h) => [...h, { ...current, value: "" }]);
    // Send answers unchanged; AI will move on to next question
    await askNext({ ...answers, [`__skipped_${current.key}`]: true });
  };

  const goBack = async () => {
    if (history.length === 0) { toast.info("No previous question"); return; }
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    // Remove that answer
    const newAns = { ...answers };
    delete newAns[prev.key];
    delete newAns[`__skipped_${prev.key}`];
    setAnswers(newAns);
    setCurrent(prev);
    setInputValue(prev.value || "");
  };

  const finalizeNow = async () => {
    await askNext(answers, true);
  };

  const restart = () => {
    setAnswers({}); setHistory([]); setDraft(""); setDraftId(""); setInputValue(""); setCurrent(null);
    setProgress({ current: 0, total: 0 }); setPhase("setup");
  };

  const copyDraft = () => {
    navigator.clipboard.writeText(draft);
    toast.success("Draft copied to clipboard");
  };

  const printDraft = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked"); return; }
    const pre = w.document.createElement("pre");
    pre.style.fontFamily = "'Segoe UI', -apple-system, sans-serif";
    pre.style.fontSize = "13px";
    pre.style.whiteSpace = "pre-wrap";
    pre.style.padding = "40px";
    pre.style.maxWidth = "760px";
    pre.style.margin = "auto";
    pre.textContent = draft;
    w.document.body.appendChild(pre);
    w.document.title = selectedTemplate?.name || "Draft";
    setTimeout(() => w.print(), 250);
  };

  const downloadDocx = async () => {
    if (!draftId) { toast.error("Draft not saved yet"); return; }
    const token = localStorage.getItem("csc_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/drafts/${draftId}/docx`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(selectedTemplate?.name || "draft").replace(/\s+/g, "_")}.docx`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const saveToCustomer = async () => {
    if (!customerId || customerId === "_none") { toast.error("Pick a customer first from setup"); return; }
    try {
      await api.post("/customer_documents", {
        customer_id: customerId,
        doc_type: "AI Draft",
        title: selectedTemplate?.name || "AI Draft",
        content: draft,
      });
      toast.success("Saved to customer profile");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    }
  };

  const pct = progress.total > 0 ? Math.min(100, Math.round((progress.current / progress.total) * 100)) : 0;

  // Build a live preview from answers as they accumulate
  const livePreview = useMemo(() => {
    if (draft) return draft;
    if (Object.keys(answers).length === 0) return "";
    const lines = [`▸ ${selectedTemplate?.name || "Draft"} (${language})`, ""];
    for (const h of history) {
      if (!h.value) continue;
      lines.push(`${h.label}\n    ${h.value}\n`);
    }
    return lines.join("\n");
  }, [answers, history, draft, selectedTemplate, language]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="interview-mode">
      {/* LEFT: setup / question */}
      <Card className="lg:col-span-2 p-5 bg-white border-slate-200 space-y-4">
        {phase === "setup" && (
          <>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2 flex items-center gap-2">
                <Robot size={14} weight="fill" /> Interview Mode
              </div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Let AI ask, you answer</h2>
              <p className="text-xs text-slate-600 mt-1">Pick a template and language, then answer one question at a time. The AI will assemble the draft for you.</p>
            </div>

            <Field label="Document *">
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="interview-template"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                <SelectContent className="max-h-96">
                  {groups.map((g) => (
                    <div key={g.group}>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-orange-600 bg-slate-50">{g.group}</div>
                      {g.items.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Customer (auto-prefills known details)">
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="interview-customer"><SelectValue placeholder="Optional — none" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="_none">— None —</SelectItem>
                  {customers.slice(0, 200).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.customer_no} · {c.name} · {c.mobile}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Language">
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="interview-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <Button onClick={startInterview} disabled={!templateId || busy} data-testid="interview-start"
              className="w-full bg-orange-600 hover:bg-orange-700 text-white gap-2 h-11">
              <Sparkle size={16} weight="fill" /> Start Interview
            </Button>
          </>
        )}

        {phase === "asking" && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-semibold">Question {progress.current} of {progress.total}</div>
                <div className="text-xs text-slate-500 mt-0.5">{selectedTemplate?.name}</div>
              </div>
              <Badge variant="outline" className="text-[10px]" data-testid="interview-progress-badge">{pct}%</Badge>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>

            {current && (
              <div className="pt-3 space-y-3">
                <div>
                  <Label className="text-slate-900 text-base font-semibold leading-snug block" data-testid="interview-question-label">
                    {current.label}
                  </Label>
                  {current.hint && <div className="text-[11px] text-slate-500 mt-1 italic">{current.hint}</div>}
                </div>

                {current.input_type === "radio" && (current.options || []).length > 0 ? (
                  <div className="space-y-2" data-testid="interview-radio-group">
                    {(current.options || []).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setInputValue(opt)}
                        className={`w-full text-left px-4 py-2.5 rounded-md border-2 text-sm transition-all ${inputValue === opt ? "border-orange-500 bg-orange-50 text-orange-900 font-medium" : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"}`}
                      >
                        <span className={`inline-block w-3.5 h-3.5 mr-2 rounded-full border-2 align-middle ${inputValue === opt ? "border-orange-500 bg-orange-500" : "border-slate-300"}`} />
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : current.input_type === "textarea" ? (
                  <Textarea rows={4} value={inputValue} onChange={(e) => setInputValue(e.target.value)} data-testid="interview-answer-input" placeholder="Type your answer…" autoFocus />
                ) : current.input_type === "date" ? (
                  <Input type="date" value={inputValue} onChange={(e) => setInputValue(e.target.value)} data-testid="interview-answer-input" autoFocus />
                ) : current.input_type === "number" ? (
                  <Input type="number" value={inputValue} onChange={(e) => setInputValue(e.target.value)} data-testid="interview-answer-input" placeholder="Number" autoFocus />
                ) : (
                  <Input value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submitAnswer(); }} data-testid="interview-answer-input" placeholder="Type your answer…" autoFocus />
                )}

                <div className="flex gap-2 flex-wrap pt-2">
                  <Button onClick={submitAnswer} disabled={busy} className="bg-orange-600 hover:bg-orange-700 text-white gap-1.5" data-testid="interview-next-btn">
                    {busy ? "AI thinking…" : <>Next <ArrowRight size={13} weight="bold" /></>}
                  </Button>
                  <Button variant="outline" onClick={goBack} disabled={busy || history.length === 0} className="gap-1.5" data-testid="interview-back-btn">
                    <ArrowLeft size={13} weight="bold" /> Back
                  </Button>
                  <Button variant="outline" onClick={skipQuestion} disabled={busy} data-testid="interview-skip-btn">Skip</Button>
                  <Button variant="outline" onClick={finalizeNow} disabled={busy} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 gap-1.5" data-testid="interview-finalize-btn">
                    <Check size={13} weight="bold" /> Finalize Now
                  </Button>
                </div>
              </div>
            )}

            {history.length > 0 && (
              <div className="pt-3 border-t border-slate-100 mt-3 space-y-1.5 max-h-64 overflow-y-auto" data-testid="interview-history">
                <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Answered</div>
                {history.map((h, i) => (
                  <div key={i} className="text-xs flex gap-2">
                    <Check size={11} weight="bold" className="text-emerald-500 mt-1 shrink-0" />
                    <div><span className="text-slate-500">{h.label}</span><br /><span className="text-slate-800 font-medium">{h.value || "(skipped)"}</span></div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {phase === "done" && (
          <>
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2">
              <Check size={18} weight="bold" className="text-emerald-600" />
              <div>
                <div className="text-sm font-semibold text-emerald-900">Draft Ready</div>
                <div className="text-[11px] text-emerald-700">Review the preview and export it below.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={copyDraft} variant="outline" className="gap-1.5 text-xs" data-testid="interview-copy-btn">
                <Copy size={13} /> Copy
              </Button>
              <Button onClick={printDraft} variant="outline" className="gap-1.5 text-xs" data-testid="interview-print-btn">
                <Printer size={13} /> Print
              </Button>
              <Button onClick={downloadDocx} variant="outline" className="gap-1.5 text-xs" data-testid="interview-docx-btn">
                <DownloadSimple size={13} /> Word (.docx)
              </Button>
              <Button onClick={saveToCustomer} variant="outline" className="gap-1.5 text-xs" disabled={!customerId || customerId === "_none"} data-testid="interview-savecust-btn">
                <FloppyDisk size={13} /> Save to Customer
              </Button>
            </div>
            <Button onClick={() => askNext(answers, true)} variant="outline" className="w-full gap-1.5 text-xs" disabled={busy} data-testid="interview-regen-btn">
              <ArrowClockwise size={13} /> Regenerate Draft
            </Button>
            <Button onClick={restart} variant="ghost" className="w-full text-xs text-slate-500 hover:text-slate-900" data-testid="interview-restart-btn">
              Start a new interview
            </Button>
          </>
        )}
      </Card>

      {/* RIGHT: live preview */}
      <Card className="lg:col-span-3 p-5 bg-slate-50 border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-2">
            {draft ? "Final Draft" : "Live Preview"}
            {phase === "asking" && !draft && <span className="text-orange-500 animate-pulse">● updating</span>}
          </div>
          {selectedTemplate && <Badge variant="outline" className="text-[10px]">{selectedTemplate.name}</Badge>}
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-6 min-h-[560px] shadow-sm">
          {!livePreview ? (
            <div className="text-center text-slate-400 py-24 text-sm">
              <Robot size={44} weight="duotone" className="mx-auto mb-3 opacity-40" />
              Pick a template on the left and start the interview.<br />
              Your answers will appear here as you go.
            </div>
          ) : (
            <pre
              className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed"
              style={{ fontFamily: draft ? "'Segoe UI', -apple-system, sans-serif" : "'IBM Plex Sans', -apple-system, sans-serif" }}
              data-testid="interview-preview"
            >
              {livePreview}
            </pre>
          )}
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">{label}</div>
      {children}
    </div>
  );
}
